import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackagePlus, Building2, Search, Package, Send, ChevronDown, Loader2,
    Trash2, X, RefreshCw, Plus,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchDirectStockCCList,
    fetchDirectStockCategories,
    fetchDirectStockSuggestions,
    addDirectStockItem,
    fetchDirectStockGrid,
    deleteDirectStockRow,
    submitDirectStock,
    clearCategories,
    clearSuggestions,
    clearGrid,
    clearSubmitResult,
    resetDirectStockUpdation,
    selectDSUCCList,
    selectDSUCategoryList,
    selectDSUSuggestions,
    selectDSUGridItems,
    selectDSUSubmitResult,
    selectDSULoading,
    selectDSUErrors,
} from '../../slices/purchaseSlice/directStockUpdationSlice';

// ── Shared UI primitives (mirrors ItemsTransfer / DailyIssue pages) ─────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// Item codes starting with '1' are Assets — legacy only allows a quantity of exactly 1
// for these rows (CountItemTotalAmount's ItemType == "1" branch).
const isAssetCode = (code) => (code || '').trim().charAt(0) === '1';

// Mirrors legacy ValidateAmountQuantity: digits, one decimal point, max 3 fraction digits.
const isValidQtyString = (v) => {
    if (v === '') return true;
    return /^\d*\.?\d{0,3}$/.test(v);
};

const DirectStockUpdationCreation = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';
    const createdBy = userData?.userName || userData?.employeeId || uid || 'system';

    const ccList       = useSelector(selectDSUCCList);
    const categoryList = useSelector(selectDSUCategoryList);
    const suggestions   = useSelector(selectDSUSuggestions);
    const gridItems      = useSelector(selectDSUGridItems);
    const submitResult   = useSelector(selectDSUSubmitResult);
    const loading         = useSelector(selectDSULoading);
    const errors           = useSelector(selectDSUErrors);

    const [selectedCC, setSelectedCC] = useState('');

    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    const [checkedRows, setCheckedRows] = useState({});
    const [qtyEdits, setQtyEdits] = useState({});

    const [updationDate, setUpdationDate] = useState(todayFormatted());
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (uid && roleId) dispatch(fetchDirectStockCCList({ uid, rid: roleId }));
        return () => dispatch(resetDirectStockUpdation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        if (!category || !selectionType || !searchText.trim()) {
            dispatch(clearSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchDirectStockSuggestions({ pfx: searchText.trim(), typ: selectionType, cat: category }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category]);

    // Reset per-row edit state whenever the grid (re)loads
    useEffect(() => {
        setCheckedRows({});
        setQtyEdits({});
    }, [gridItems]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`Request No ${submitResult.refNo}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center selection ───────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearSuggestions());
        dispatch(clearCategories());
        dispatch(clearGrid());
        if (cc) {
            dispatch(fetchDirectStockCategories({ ccode: cc }));
            dispatch(fetchDirectStockGrid({ ccode: cc }));
        }
    };

    // ── Item search ──────────────────────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { toast.error('Select Search By Category'); return; }
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.SearchItem || '');
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleAddItem = () => {
        if (!category)      { toast.error('Select Search By Category'); return; }
        if (!selectionType) { toast.error('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.error('Enter Search Data'); return; }
        if (!selectedCC)     { toast.error('Select Cost/Job Center Code'); return; }

        const itemCode = searchText.split(',')[0].trim();
        dispatch(addDirectStockItem({ ItemCode: itemCode, Costcenter: selectedCC })).then((result) => {
            if (addDirectStockItem.fulfilled.match(result)) {
                setSearchText('');
                setShowSuggestions(false);
                dispatch(fetchDirectStockGrid({ ccode: selectedCC }));
            } else {
                toast.error(result.payload || 'Failed to add item');
            }
        });
    };

    // ── Grid row edits ───────────────────────────────────────────────────────

    const toggleRowChecked = (rid) => setCheckedRows((prev) => ({ ...prev, [rid]: !prev[rid] }));

    const handleQtyChange = (row, value) => {
        if (!isValidQtyString(value)) return;
        if (isAssetCode(row.ItemCode) && value !== '' && num(value) !== 1) {
            toast.error('Invalid Quantity');
            setQtyEdits((prev) => ({ ...prev, [row.DirectStockId]: '' }));
            return;
        }
        setQtyEdits((prev) => ({ ...prev, [row.DirectStockId]: value }));
    };

    const amountFor = (row) => {
        const qty = qtyEdits[row.DirectStockId];
        if (qty === undefined || qty === '') return 0;
        if (isAssetCode(row.ItemCode)) {
            return num(qty) === 1 ? num(row.BasicPrice) : 0;
        }
        return num(row.BasicPrice) * num(qty);
    };

    const handleDeleteRow = (row) => {
        if (!checkedRows[row.DirectStockId]) { toast.error('Please Check for Delete'); return; }
        dispatch(deleteDirectStockRow({ DirectStockId: row.DirectStockId })).then((result) => {
            if (deleteDirectStockRow.fulfilled.match(result)) {
                toast.success('Deleted Successfully');
                dispatch(fetchDirectStockGrid({ ccode: selectedCC }));
            } else {
                toast.error(result.payload || 'Failed to delete item');
            }
        });
    };

    const subTotal = useMemo(
        () => gridItems.reduce((sum, row) => sum + amountFor(row), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridItems, qtyEdits]
    );

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!selectedCC)      { toast.error('Select Cost/Job Center Code'); return; }
        if (!updationDate)     { toast.error('Select Date'); return; }
        if (!remarks.trim())   { toast.error('Select Remarks'); return; }
        if (gridItems.length === 0) { toast.error('Please Add Direct Stock Items'); return; }
        if (!gridItems.every((r) => checkedRows[r.DirectStockId])) { toast.error('Please Verify Item Codes'); return; }
        if (!gridItems.every((r) => num(qtyEdits[r.DirectStockId]) > 0)) { toast.error('Enter Quantity'); return; }

        let rowids = '', quantitys = '', amounts = '', basics = '';
        gridItems.forEach((row) => {
            rowids    += `${row.DirectStockId},`;
            quantitys += `${qtyEdits[row.DirectStockId]},`;
            amounts   += `${amountFor(row)},`;
            basics    += `${row.BasicPrice},`;
        });

        dispatch(submitDirectStock({
            DirectStockId: rowids,
            Quantity: quantitys,
            Amount: amounts,
            BasicPrice: basics,
            Costcenter: selectedCC,
            Date: updationDate,
            Remarks: remarks,
            RoleID: String(roleId),
            Createdby: createdBy,
        }));
    };

    const handleReset = () => {
        setSelectedCC('');
        setCategory(''); setSelectionType(''); setSearchText('');
        setCheckedRows({}); setQtyEdits({});
        setUpdationDate(todayFormatted());
        setRemarks('');
        dispatch(clearCategories());
        dispatch(clearSuggestions());
        dispatch(clearGrid());
        if (uid && roleId) dispatch(fetchDirectStockCCList({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = gridItems.length > 0;
    const checkedCount = gridItems.filter((r) => checkedRows[r.DirectStockId]).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <PackagePlus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Direct Stock Updation</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Add stock items directly into a cost center's inventory.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Cost/Job Center Code</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {loading.grid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search ──────────────────────────────────────────────── */}
            {selectedCC && (
                <SectionCard icon={Search} title="Search &amp; Add Item" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[200px]">
                            <Label required>Search By Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange} disabled={loading.categoryList}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label required>Search By Type</Label>
                            <select className={inputCls} value={selectionType} onChange={handleSelectionTypeChange} disabled={!category}>
                                <option value="">-Please Select-</option>
                                {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative flex-1 min-w-[260px]" ref={searchBoxRef}>
                            <Label required>Search Here</Label>
                            <input type="text" disabled={!selectionType} placeholder="Start typing to search…"
                                className={`${inputCls} uppercase`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                onFocus={() => searchText && setShowSuggestions(true)} />
                            {loading.suggestions && (
                                <Loader2 className="absolute right-3 top-9 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                    {suggestions.map((s, i) => (
                                        <button key={i} type="button" onClick={() => handlePickSuggestion(s)}
                                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                                            {s.SearchItem}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleAddItem} disabled={loading.add}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.add ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Staged items grid ────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title="Direct Stock Items"
                    subtitle={`${checkedCount} of ${gridItems.length} item${gridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Basic Price', 'Units', 'Quantity', 'Amount', ''].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridItems.map((row, idx) => (
                                    <tr key={row.DirectStockId ?? idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                        <td className="px-2.5 py-2">
                                            <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                checked={!!checkedRows[row.DirectStockId]} onChange={() => toggleRowChecked(row.DirectStockId)} />
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.ItemCode}</td>
                                        <td className="px-2.5 py-2 text-gray-900 dark:text-white">{row.ItemName}</td>
                                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{row.Specification}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.DcaCode}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.SubDcaCode}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{row.BasicPrice}</td>
                                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{row.Units}</td>
                                        <td className="px-1.5 py-1.5 w-24">
                                            <input type="text" inputMode="decimal"
                                                className={`${inputCls} text-right`}
                                                placeholder={isAssetCode(row.ItemCode) ? '1' : ''}
                                                value={qtyEdits[row.DirectStockId] ?? ''}
                                                onChange={(e) => handleQtyChange(row, e.target.value)} />
                                        </td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{amountFor(row).toFixed(2)}</td>
                                        <td className="px-1.5 py-1.5">
                                            <button onClick={() => handleDeleteRow(row)} disabled={loading.deleteItem}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-indigo-50/60 dark:bg-indigo-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                    <td colSpan={8} className="px-2.5 py-2 text-right">SubTotal</td>
                                    <td className="px-2.5 py-2 text-right">{subTotal.toFixed(2)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Date</Label>
                            <CustomDatePicker value={updationDate} onChange={setUpdationDate}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={todayFormatted()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default DirectStockUpdationCreation;
